import React, { useEffect, useState } from 'react';

import Modal from '@/ui-components/Modal';
import Panel, { PanelClose } from '@/ui-components/Panel';
import ProfileScreen from '@/ui-components/ProfileScreen';
import Loading from '@/ui-components/Loading';
import Typography from '@/ui-components/Typography';

import useBem from '@/use/useBem';
import useModal, { openModal } from '@/use/useModal';

import * as profile from '@/api/profile.service';
import { IUser } from '@/framework/user';

const UserInfoModal = () => {
  const [user, setUser] = useState<IUser | null>(null);
  const [error, setError] = useState(false);
  const [returnTo, setReturnTo] = useState<{
    modalId: string;
    props?: Parameters<typeof openModal>[1];
  } | null>(null);
  const { bem } = useBem('user-info');
  const [isOpen, setModal, props] = useModal('user-info');

  const userId = props?.extra?.userId;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextReturn = props?.extra?.returnTo;
    setReturnTo(
      nextReturn?.modalId
        ? { modalId: nextReturn.modalId, props: nextReturn.props }
        : null,
    );

    if (!userId) {
      setUser(null);
      setError(true);
      return;
    }

    let cancelled = false;
    setUser(null);
    setError(false);

    profile
      .getUserById(String(userId))
      .then(({ data }) => {
        if (cancelled) return;
        if (data.id == null && !data.username) {
          setError(true);
          return;
        }
        setUser(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, userId, props?.extra?.returnTo]);

  const handleClose = () => {
    setModal.close();
  };

  const handleAfterClose = () => {
    setUser(null);
    setError(false);
    if (returnTo?.modalId) {
      openModal(returnTo.modalId, returnTo.props);
    }
    setReturnTo(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      isCentered
      onRequestClose={handleClose}
      onAfterClose={handleAfterClose}
      contentClassName={bem('')}
    >
      <Panel>
        <PanelClose onClick={handleClose} />

        {error ? (
          <Typography variant="m">Could not load this profile.</Typography>
        ) : user ? (
          <ProfileScreen {...{ user }} />
        ) : (
          <Loading />
        )}
      </Panel>
    </Modal>
  );
};

export default UserInfoModal;
